import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import PageHeaderActionsBar from './ui/PageHeaderActionsBar';
import {
  Typography,
  Grid,
  Box,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import api, { enhancedApi } from '../api';
import { Description as StatementIcon, FilterList as FilterIcon, FileDownload as ExportIcon, Visibility } from '@mui/icons-material';

const getStatusColor = (status) => {
  switch (status) {
    case 'Paid': return 'success';
    case 'Pending': return 'warning';
    case 'Overdue': return 'error';
    default: return 'default';
  }
};

const BillingStatements = () => {
  const { userPermissions } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [filterBy, setFilterBy] = useState('All Time');
  const [status, setStatus] = useState('All Invoices');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchClients = async () => {
    try {
      const response = await enhancedApi.getClients();
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.clients || data?.data || []);
      setClients(list.map(c => ({ id: c.id, name: c.display_name || c.primary_contact?.display_name || `Client ${c.id}` })));
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients');
      setClients([]);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/billing/all-invoices/');
      const list = response.data?.invoices || [];
      setInvoices(list);
      setFilteredInvoices(list);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userPermissions?.can_access_billing_settings) {
      setError('No access to billing features.');
      setLoading(false);
      return;
    }
    fetchClients();
    fetchInvoices();
  }, [userPermissions]);

  const applyFilters = () => {
    let result = [...invoices];

    if (selectedClient) {
      result = result.filter(inv => String(inv.client_id) === String(selectedClient));
    }

    if (filterBy !== 'All Time') {
      const now = new Date();
      let cutoff;
      if (filterBy === 'Last Month') {
        cutoff = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      } else {
        cutoff = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      }
      result = result.filter(inv => {
        const d = inv.invoice_date ? new Date(inv.invoice_date) : null;
        return d && d >= cutoff;
      });
    }

    if (status === 'Outstanding') {
      result = result.filter(inv => (inv.balance ?? inv.total) > 0);
    }

    setFilteredInvoices(result);
  };

  const exportToCSV = () => {
    const data = filteredInvoices;
    if (data.length === 0) {
      return;
    }
    const headers = ['Invoice #', 'Client', 'Amount', 'Balance', 'Status', 'Invoice Date', 'Due Date'];
    const rows = data.map(inv => [
      inv.invoice_number || '',
      inv.client_name || '',
      inv.total ?? 0,
      inv.balance ?? inv.total ?? 0,
      inv.status || '',
      inv.invoice_date || '',
      inv.due_date || ''
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `statements-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
    setDetailLoading(true);
    setInvoiceDetail(null);
    try {
      const response = await api.get(`/api/billing/invoice-detail/${invoice.id}/`);
      setInvoiceDetail(response.data);
    } catch (err) {
      console.error('Error fetching invoice detail:', err);
      setInvoiceDetail({ error: 'Failed to load invoice' });
    } finally {
      setDetailLoading(false);
    }
  };

  const displayInvoices = filteredInvoices;

  return (
    <Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}
      <PageHeaderActionsBar title="Billing Statements" description="Filter and export invoice statements" />
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Statement Options</Typography>
        <Grid container spacing={3} alignItems="center" wrap="wrap">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Client</InputLabel>
              <Select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                displayEmpty
                label="Client"
              >
                <MenuItem value="">All Clients</MenuItem>
                {clients.map(client => (
                  <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Filter By</InputLabel>
              <Select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                displayEmpty
                label="Filter By"
              >
                <MenuItem value="All Time">All Time</MenuItem>
                <MenuItem value="Last Month">Last Month</MenuItem>
                <MenuItem value="Last Year">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                displayEmpty
                label="Status"
              >
                <MenuItem value="All Invoices">All Invoices</MenuItem>
                <MenuItem value="Outstanding">Outstanding</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterIcon />}
              onClick={applyFilters}
              disabled={loading}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Filter
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={exportToCSV}
              disabled={loading || invoices.length === 0}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center' }}>
                      <StatementIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2, display: 'block', mx: 'auto' }} />
                      <Typography variant="body1" color="text.secondary" gutterBottom>No invoices to display</Typography>
                      <Typography variant="body2" color="text.secondary">Apply filters or check back when invoices are available</Typography>
                    </TableCell>
                  </TableRow>
                ) : displayInvoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.client_name}</TableCell>
                    <TableCell>${(invoice.total ?? 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip label={invoice.status || 'Draft'} color={getStatusColor(invoice.status)} size="small" />
                    </TableCell>
                    <TableCell>{invoice.invoice_date}</TableCell>
                    <TableCell>{invoice.due_date}</TableCell>
                    <TableCell>
                      <IconButton size="small" title="View" onClick={() => handleViewInvoice(invoice)}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>{selectedInvoice ? `Invoice #${selectedInvoice.invoice_number}` : 'Invoice'}</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : invoiceDetail?.error ? (
            <Typography color="error">{invoiceDetail.error}</Typography>
          ) : invoiceDetail?.pdf ? (
            <Box sx={{ height: 500 }}>
              <iframe
                title="Invoice PDF"
                src={`data:application/pdf;base64,${invoiceDetail.pdf}`}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingStatements;